/**
 * Domain types for the Lists feature.
 * All server-side snake_case fields are mapped to camelCase here.
 */
import type { ProductImages } from "modules/product/types/product";

/** Distinguishes between free-text items and catalog-linked items */
export enum ListItemType {
    FREE = 'free',
    LINKED = 'linked',
}

/** Lifecycle states of a list execution */
export type ListExecutionStatus =
    | 'resolving'
    | 'draft'
    | 'confirmed'
    | 'sent_to_cart'
    | 'completed'
    | 'cancelled';

/** Pipeline steps streamed by the SSE progress channel during list execution. */
export type ProgressStep =
    | 'starting'
    | 'embedding'
    | 'intent_search'
    | 'llm_batch'
    | 'completed'
    | 'failed';

/**
 * Parsed state of one SSE progress event.
 * The terminal `completed` event carries the authoritative `ListExecution`
 * in `payload` — the new frontend flow executes the list through the SSE
 * stream endpoint only and applies this payload as the final result.
 * Non-terminal and `failed` events always have `payload: null`.
 */
export interface ProgressState {
    step: ProgressStep;
    /** Overall completion estimate, 0-100. */
    percentage: number;
    /** User-facing message for the current step. */
    message: string;
    /** "N/M" while llm_batch validates batch N of M; null otherwise. */
    batchInfo?: string | null;
    /** Final ListExecution — populated only by the terminal completed event. */
    payload?: ListExecution | null;
    /**
     * Execution id carried by the terminal `completed` event (mirrors
     * `payload.id`). Recovery key: when the completed event arrives without a
     * usable payload, the service fetches the persisted execution by this id
     * with a read-only GET — never a re-execution.
     */
    executionId?: string | null;
}

/**
 * A single product candidate returned by the AI resolver for a free-text list item.
 */
export interface CandidateProduct {
    id: string;
    catalogProductId: number;
    /** Canonical product SKU used as productDecision.productId. */
    canonicalSku: string;
    /** Product slug for detail navigation / product sheet. */
    slug: string;
    name: string;
    brand: string;
    /** Legacy single-image URL — falls back to `images.catalog`. */
    imageUrl: string | null;
    /** Structured image set from the API (thumbs/catalog/detail). */
    images?: ProductImages | null;
    price: number | null;
    unit: string | null;
    confidenceScore: number;
    rank: number;
    selected: boolean;
    /** Hashed SKU from the API - may be null if not yet resolved */
    sku?: string | null;
}

/**
 * Represents one item inside a shopping list.
 * - `type === FREE`   → rawDescription is a user-typed description; catalogProductId is null.
 * - `type === LINKED` → catalogProductId references a known SKU in the catalog.
 */
export interface ListItem {
    id: string;
    listId: string;
    rawDescription: string;
    catalogProductId: string | null;
    linkedProductName: string | null;
    catalogProductSku: string | null;
    itemImgUrl: string | null;
    categoryId: number | null;
    quantity: number;
    unit: string | null;
    notes: string | null;
    sortOrder: number;
    type: ListItemType;
}

/**
 * A resolved list item with its candidate fields merged inline.
 * Replaces the nested ItemWithCandidates structure.
 */
export interface ResolvedListItem extends ListItem {
    /** Catalog product name from selected candidate (falls back to rawDescription). */
    name: string | null;
    brand: string | null;
    imageUrl: string | null;
    price: number | null;
    sku: string | null;
    confidenceScore: number | null;
    selectedCandidateId: string | null;
    /**
     * Reason why this item could not be resolved, if applicable.
     * - null: item resolved successfully
     * - 'no_candidates_found': free item exhausted all resolution paths
     * - 'linked_product_not_found': linked item's catalog_product_id is invalid
     */
    missedReason: string | null;
    /**
     * Product candidates the AI resolver considered for this free-text item.
     * These double as the replacement alternatives shown in the cart sheet
     * and the replacement preferences dialog.
     */
    candidates: CandidateProduct[];
}

/** Aggregated category data used for list card previews */
export interface ListCategorySummary {
    categoryId: number | null;
    itemCount: number;
}

/** Compact representation used in list cards and collection views */
export interface List {
    id: string;
    name: string;
    description: string | null;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    itemCount: number;
    categorySummaries: ListCategorySummary[];
}

/** Full list including all its items — used for the detail panel */
export interface ListDetail extends List {
    items: ListItem[];
    /**
     * True when free-text items are missing parsed_intent / intent_embeddings
     * and the frontend should start the migration stream on load.
     */
    migrationRequired: boolean;
    /** Count of free-text items missing intent metadata (migration_total). */
    migrationTotal: number;
    /** Count of free-text items already fully migrated (migration_completed). */
    migrationCompleted: number;
}

/**
 * Pipeline steps streamed by the SSE migration channel. Deliberately separate
 * from `ProgressStep`: the execution contract is shared with list-execute-sdd
 * and must not drift.
 */
export type MigrationStep =
    | 'migration_item'
    | 'migration_completed'
    | 'migration_partial';

/** Parsed state of one SSE migration event. */
export interface MigrationState {
    step: MigrationStep;
    /** Cumulative completion estimate, 0-100 (0-99 per item, 100 terminal). */
    percentage: number;
    /** User-facing message (the item description while migrating). */
    message: string;
    /** Item being migrated — only migration_item events populate it. */
    itemId?: string | null;
    /** Failed item count — only migration_partial populates it. */
    failedCount?: number | null;
    /** Total item count — only terminal events populate it. */
    totalCount?: number | null;
}

/** Lifecycle states of the migration stream. */
export type MigrationStreamStatus =
    | 'idle'
    | 'streaming'
    | 'completed'
    | 'partial'
    | 'error';

/**
 * A temporary item with resolved candidates from the backend.
 * Used for free-text items sent in the execute payload.
 */
export interface TemporaryExecutionItem {
    tempKey: string;
    rawDescription: string;
    quantity: number;
    candidates: CandidateProduct[];
}

/**
 * Represents an execution instance of a shopping list.
 * Created when the user taps "Ejecutar Lista".
 */
export interface ListExecution {
    id: string;
    listId: string;
    status: ListExecutionStatus;
    itemsWithCandidates: ResolvedListItem[];
    /** Items that could not be resolved — mutually exclusive with itemsWithCandidates */
    missedItems: ResolvedListItem[];
    temporaryItemsWithCandidates: ResolvedListItem[];
    cartId: string | null;
}

/** Summary view of a past execution — used in execution history */
export interface ListExecutionSummary {
    id: string;
    listId: string;
    status: ListExecutionStatus;
    createdAt: string;
    cartId: string | null;
}

/** Payload to create or update a list item */
export interface CreateListItemPayload {
    rawDescription: string;
    catalogProductId?: string | null;
    /** hashed_sku from the catalog — used when the catalog id is unreliable (e.g. GCR-generated). Backend resolves to the real products.id. */
    catalogProductSku?: string | null;
    categoryId?: number | null;
    quantity: number;
    unit?: string | null;
    notes?: string | null;
    /** Display name of the selected catalog product. Passed to parent for temporary quote items. */
    name?: string | null;
}

/**
 * A product added temporarily in view mode for quote purposes only.
 * Not persisted to the list — discarded when navigating away.
 *
 * When catalogProductId is null, the item is a free-text entry and rawDescription
 * carries the user's typed input. Linked items (catalogProductId set) behave as before.
 */
export interface TemporaryQuoteItem {
    /** Unique key for React rendering (crypto.randomUUID). */
    key: string;
    /** Catalog product ID when linked to a known product; null for free-text items. */
    catalogProductId: string | null;
    sku: string | null;
    name: string;
    categoryId: number;
    imageUrl: string | null;
    quantity: number;
    /** User's raw free-text description — set only when catalogProductId is null. */
    rawDescription?: string;
}

/**
 * A list item shown optimistically while its backend save is still in flight.
 * Rendered in edit mode so the user immediately sees their selection; it is
 * replaced by the real item (or marked as failed) once the save settles.
 */
export interface OptimisticListItem {
    /** Temporary key for React rendering (crypto.randomUUID). */
    key: string;
    rawDescription: string;
    catalogProductId: string | null;
    catalogProductSku: string | null;
    categoryId: number | null;
    quantity: number;
    unit: string | null;
    name: string | null;
    type: ListItemType;
    /** pending while the save runs; error when the backend rejected it. */
    status: "pending" | "error";
}

/**
 * A product candidate returned by the item search endpoint.
 * Mirrors the backend `ItemSearchResult` schema.
 */
export interface ItemSearchResult {
    id: string;
    s_id: number;
    name: string;
    brand: string;
    imageUrl: string | null;
    price: number | null;
    unit: string | null;
    total: number | null;
    categoryId: number;
    parsedQuantity: number;
    parsedUnit: string | null;
    /** hashed_sku — used by getPrice on add-to-cart. */
    sku: string | null;
}
